import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Snackbar,
  Breadcrumbs,
  Link,
  Fab,
  Avatar,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  Divider,
  Tooltip,
  InputAdornment,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  Home as HomeIcon,
  People as UsersIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
  ContentCut as MasterIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Upload as UploadIcon,
  CloudUpload as CloudUploadIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";

const getApiBase = () => {
  const envBase = process.env.REACT_APP_API_URL;
  if (!envBase) return "/api";
  if (
    envBase.startsWith("http://") &&
    typeof window !== "undefined" &&
    window.location.protocol === "https:"
  ) {
    return envBase.replace(/^http:\/\//, "https://");
  }
  return envBase;
};

const API_BASE = getApiBase();

const asErrorMessage = (detail: any) => {
  if (!detail) return "";
  if (typeof detail === "string") return detail;
  try {
    return JSON.stringify(detail);
  } catch {
    return String(detail);
  }
};

interface ServiceCategory {
  id: number;
  name: string;
}

interface User {
  id: number;
  full_name: string | null;
  phone: string;
  email: string | null;
  username: string | null;
  role: "admin" | "master" | "manager" | "client";
  is_active: boolean;
  is_verified: boolean;
  created_at: string;
  last_login?: string;

  photo_url?: string | null;
  working_hours?: any;
  specialization_category_ids?: number[] | null;

  can_cancel?: boolean | null;
  can_reschedule?: boolean | null;
}

interface CreateUserRequest {
  full_name: string;
  phone: string;
  role: "admin" | "master";
  email?: string;
  username?: string;

  photo_url?: string;
  working_hours?: any;
  specialization_category_ids?: number[];

  can_cancel?: boolean | null;
  can_reschedule?: boolean | null;
}

interface Location {
  id: number;
  name: string;
  address: string;
}

export default function UsersPage() {
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [serviceCategories, setServiceCategories] = useState<ServiceCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<string>("");
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState<"all" | "master" | "admin">("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [formData, setFormData] = useState<CreateUserRequest>({
    full_name: "",
    phone: "",
    email: "",
    username: "",
    role: "master",
  });
  const dayNames = {
  monday: "Понедельник",
  tuesday: "Вторник",
  wednesday: "Среда",
  thursday: "Четверг",
  friday: "Пятница",
  saturday: "Суббота",
  sunday: "Воскресенье",
};
  const [alert, setAlert] = useState({ show: false, message: "", severity: "success" as "success" | "error" });
  const [dialogTab, setDialogTab] = useState(0);

  // Загрузка пользователей
  useEffect(() => {
    loadUsers();
    loadLocations();
    loadServiceCategories();
  }, []);

  const loadServiceCategories = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE}/services/public/categories`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });

      if (response.ok) {
        const data = await response.json();
        const cats = (data?.categories || []).map((c: any) => ({ id: c.id, name: c.name }));
        setServiceCategories(cats);
      } else {
        setServiceCategories([]);
      }
    } catch (e) {
      console.error("Error loading service categories:", e);
      setServiceCategories([]);
    }
  };

  const loadUsers = async () => {
    try {
      setLoading(true);

      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE}/users/`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        const usersData: User[] = data.map((user: any) => ({
          id: user.id,
          full_name: user.full_name,
          phone: user.phone,
          email: user.email,
          username: user.username,
          role: user.role,
          is_active: user.is_active,
          is_verified: user.is_verified,
          created_at: user.created_at,
          last_login: user.last_login,

          photo_url: user.photo_url || null,
          working_hours: user.working_hours || null,
          specialization_category_ids: user.specialization_category_ids || null,

          can_cancel: user.can_cancel ?? null,
          can_reschedule: user.can_reschedule ?? null,
        }));
        setUsers(usersData);
        setError("");
      } else {
        const errorData = await response.json();
        setError(asErrorMessage(errorData.detail) || "Ошибка загрузки пользователей");
      }

    } catch (error) {
      console.error("Error loading users:", error);
      setError("Ошибка подключения к серверу");
    } finally {
      setLoading(false);
    }
  };

  const loadLocations = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE}/locations/`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        const locationsArray = data.locations || data;
        const locationsData: Location[] = locationsArray.map((loc: any) => ({
          id: loc.id,
          name: loc.name,
          address: loc.physical_address || loc.legal_address,
        }));
        setLocations(locationsData);
      } else {
        setLocations([]);
      }
    } catch (error) {
      console.error("Error loading locations:", error);
      setLocations([]);
    }
  };

  const handleCreateUser = () => {
    setEditingUser(null);
    setFormData({
      full_name: "",
      phone: "",
      role: "master",
      specialization_category_ids: [],
      working_hours: {
        monday: { works: true, open: "09:00", close: "21:00" },
        tuesday: { works: true, open: "09:00", close: "21:00" },
        wednesday: { works: true, open: "09:00", close: "21:00" },
        thursday: { works: true, open: "09:00", close: "21:00" },
        friday: { works: true, open: "09:00", close: "21:00" },
        saturday: { works: true, open: "09:00", close: "21:00" },
        sunday: { works: false, open: "09:00", close: "21:00" },
      },
    });
    setDialogOpen(true);
  };

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    const formData: CreateUserRequest = {
      full_name: user.full_name || "",
      phone: user.phone,
      role: user.role === "client" ? "master" : user.role as "admin" | "master",
      specialization_category_ids: user.specialization_category_ids || [],
      working_hours: user.working_hours || {
        monday: { works: true, open: "09:00", close: "21:00" },
        tuesday: { works: true, open: "09:00", close: "21:00" },
        wednesday: { works: true, open: "09:00", close: "21:00" },
        thursday: { works: true, open: "09:00", close: "21:00" },
        friday: { works: true, open: "09:00", close: "21:00" },
        saturday: { works: true, open: "09:00", close: "21:00" },
        sunday: { works: false, open: "09:00", close: "21:00" },
      },
      can_cancel: user.can_cancel ?? null,
      can_reschedule: user.can_reschedule ?? null,
    };

    // Добавляем опциональные поля только если они существуют
    if (user.email) {
      formData.email = user.email;
    }
    if (user.username) {
      formData.username = user.username;
    }

    setFormData(formData);
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      full_name: "",
      phone: "",
      role: "master",
      specialization_category_ids: [],
      working_hours: {
        monday: { works: true, open: "09:00", close: "21:00" },
        tuesday: { works: true, open: "09:00", close: "21:00" },
        wednesday: { works: true, open: "09:00", close: "21:00" },
        thursday: { works: true, open: "09:00", close: "21:00" },
        friday: { works: true, open: "09:00", close: "21:00" },
        saturday: { works: true, open: "09:00", close: "21:00" },
        sunday: { works: false, open: "09:00", close: "21:00" },
      },
    });
    setEditingUser(null);
  };

  const handleUploadPhoto = async (file: File) => {
    if (!editingUser) return;
    try {
      const token = localStorage.getItem("token");
      const form = new FormData();
      form.append("file", file);

      const response = await fetch(`${API_BASE}/users/${editingUser.id}/upload-photo`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        body: form,
      });

      if (response.ok) {
        const data = await response.json();
        setFormData(prev => ({ ...prev, photo_url: data.photo_url }));
        setAlert({ show: true, message: "Фото загружено", severity: "success" });
        loadUsers();
      } else {
        const err = await response.json();
        setAlert({ show: true, message: err?.detail || "Ошибка загрузки фото", severity: "error" });
      }
    } catch (e) {
      console.error("Upload photo error:", e);
      setAlert({ show: true, message: "Ошибка загрузки фото", severity: "error" });
    }
  };

  const handleSaveUser = async () => {
    try {
      const token = localStorage.getItem("token");

      const url = editingUser
        ? `${API_BASE}/users/${editingUser.id}`
        : `${API_BASE}/users/`;
      const method = editingUser ? 'PUT' : 'POST';

      const userData: any = {
        full_name: formData.full_name,
        phone: formData.phone,
        role: formData.role,
      };

      if (formData.role === "master") {
        userData.specialization_category_ids = formData.specialization_category_ids || [];
        userData.working_hours = formData.working_hours || null;
        if (formData.photo_url) {
          userData.photo_url = formData.photo_url;
        }

        if (formData.can_cancel !== undefined) {
          userData.can_cancel = formData.can_cancel;
        }
        if (formData.can_reschedule !== undefined) {
          userData.can_reschedule = formData.can_reschedule;
        }
      }

      // Добавляем поля только если они не пустые
      if (formData.email) {
        userData.email = formData.email;
      }
      if (formData.username) {
        userData.username = formData.username;
      }

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(userData),
      });

      if (response.ok) {
        setAlert({
          show: true,
          message: editingUser ? "Пользователь обновлен" : "Пользователь создан",
          severity: "success"
        });
        setDialogOpen(false);
        setEditingUser(null);
        resetForm();
        loadUsers(); // Обновляем список
      } else {
        const errorData = await response.json();
        const errorMessage = typeof errorData.detail === 'string'
          ? errorData.detail
          : (errorData.detail?.msg || 'Ошибка сохранения');
        setAlert({ show: true, message: errorMessage, severity: "error" });
      }

    } catch (error) {
      console.error("Error saving user:", error);
      setAlert({ show: true, message: "Ошибка подключения к серверу", severity: "error" });
    }
  };

  const handleDeleteUser = async (userId: number) => {
    if (!window.confirm("Вы уверены, что хотите удалить этого пользователя?")) {
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE}/users/${userId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setAlert({ show: true, message: "Пользователь удален", severity: "success" });
        loadUsers(); // Обновляем список
      } else {
        const errorData = await response.json();
        setAlert({ show: true, message: errorData.detail || "Ошибка удаления", severity: "error" });
      }

    } catch (error) {
      console.error("Error deleting user:", error);
      setAlert({ show: true, message: "Ошибка подключения к серверу", severity: "error" });
    }
  };

  const handleToggleStatus = async (user: User) => {
    try {
      const token = localStorage.getItem("token");
      const newStatus = user.is_active ? false : true;

      const response = await fetch(`${API_BASE}/users/${user.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ is_active: newStatus }),
      });

      if (response.ok) {
        setAlert({
          show: true,
          message: `Пользователь ${newStatus ? "активирован" : "деактивирован"}`,
          severity: "success"
        });
        loadUsers(); // Обновляем список
      } else {
        const errorData = await response.json();
        setAlert({ show: true, message: errorData.detail || "Ошибка изменения статуса", severity: "error" });
      }

    } catch (error) {
      console.error("Error toggling status:", error);
      setAlert({ show: true, message: "Ошибка подключения к серверу", severity: "error" });
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case "admin":
        return <AdminIcon />;
      case "master":
        return <MasterIcon />;
      case "manager":
        return <PersonIcon />;
      default:
        return <PersonIcon />;
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case "admin":
        return "#9333ea";
      case "master":
        return "#0ea5e9";
      case "manager":
        return "#f59e0b";
      default:
        return "#64748b";
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case "admin":
        return "Администратор";
      case "master":
        return "Мастер";
      case "manager":
        return "Управляющий";
      case "client":
        return "Клиент";
      default:
        return role;
    }
  };

  const getSpecialization = (user: User): string | null => {
    if (user.role === "master" && (user.specialization_category_ids?.length || 0) > 0) {
      const names = serviceCategories
        .filter((c) => (user.specialization_category_ids || []).includes(c.id))
        .map((c) => c.name);
      return names.length ? names.join(", ") : null;
    }
    return null;
  };

  // ---- График работы ----
  const DAY_DEFS: { key: keyof typeof dayNames; short: string }[] = [
    { key: "monday", short: "Пн" },
    { key: "tuesday", short: "Вт" },
    { key: "wednesday", short: "Ср" },
    { key: "thursday", short: "Чт" },
    { key: "friday", short: "Пт" },
    { key: "saturday", short: "Сб" },
    { key: "sunday", short: "Вс" },
  ];
  const WEEK_KEYS: (keyof typeof dayNames)[] = [
    "sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday",
  ];
  const TODAY_KEY = WEEK_KEYS[new Date().getDay()];

  const parseHours = (wh: any): any => {
    if (!wh) return null;
    if (typeof wh === "string") {
      try { return JSON.parse(wh); } catch { return null; }
    }
    return wh;
  };
  const hourNum = (s?: string): string => {
    if (!s) return "";
    return String(parseInt(String(s).split(":")[0], 10));
  };
  const dayHours = (wh: any, key: string): { open: string; close: string } | null => {
    const h = parseHours(wh);
    const d = h?.[key];
    if (!d || !d.works) return null;
    return { open: d.open || "09:00", close: d.close || "21:00" };
  };
  const workingDaysCount = (wh: any): number => {
    const h = parseHours(wh);
    if (!h) return 0;
    return DAY_DEFS.filter((d) => h?.[d.key]?.works).length;
  };

  // ---- Фильтрация / сортировка / группировка ----
  const roleRank: Record<string, number> = { admin: 0, manager: 1, master: 2, client: 3 };
  const visibleUsers = users
    .filter((u) => (roleFilter === "all" ? true : u.role === roleFilter))
    .filter((u) => {
      const q = search.trim().toLowerCase();
      if (!q) return true;
      return (
        (u.full_name || "").toLowerCase().includes(q) ||
        (u.phone || "").toLowerCase().includes(q) ||
        (u.email || "").toLowerCase().includes(q) ||
        getRoleLabel(u.role).toLowerCase().includes(q)
      );
    })
    .sort((a, b) => {
      if (roleRank[a.role] !== roleRank[b.role]) return (roleRank[a.role] ?? 9) - (roleRank[b.role] ?? 9);
      if (a.is_active !== b.is_active) return a.is_active ? -1 : 1;
      return (a.full_name || "").localeCompare(b.full_name || "", "ru");
    });

  const groups = [
    { role: "admin", title: "Администраторы", items: visibleUsers.filter((u) => u.role === "admin") },
    { role: "master", title: "Мастера", items: visibleUsers.filter((u) => u.role === "master") },
    { role: "other", title: "Прочие", items: visibleUsers.filter((u) => u.role !== "admin" && u.role !== "master") },
  ].filter((g) => g.items.length > 0);

  const stats = [
    { label: "Всего", value: users.length, icon: <UsersIcon />, color: "#4f46e5" },
    { label: "Мастеров", value: users.filter((u) => u.role === "master").length, icon: <MasterIcon />, color: "#0ea5e9" },
    { label: "Администраторов", value: users.filter((u) => u.role === "admin").length, icon: <AdminIcon />, color: "#9333ea" },
    { label: "Активных", value: users.filter((u) => u.is_active).length, icon: <CheckCircleIcon />, color: "#16a34a" },
  ];

  const renderUserCard = (user: User) => {
    const color = getRoleColor(user.role);
    const isMaster = user.role === "master";
    const spec = getSpecialization(user);
    const today = isMaster ? dayHours(user.working_hours, TODAY_KEY) : null;
    const hasSchedule = isMaster && workingDaysCount(user.working_hours) > 0;

    return (
      <Paper
        variant="outlined"
        sx={{
          p: 2.5,
          borderRadius: 3,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          transition: "box-shadow .2s ease, border-color .2s ease, transform .2s ease",
          opacity: user.is_active ? 1 : 0.6,
          "&:hover": {
            boxShadow: "0 8px 28px rgba(15,23,42,0.08)",
            borderColor: color + "66",
            transform: "translateY(-2px)",
          },
        }}
      >
        {/* Шапка карточки */}
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
          <Box sx={{ position: "relative", flexShrink: 0 }}>
            <Avatar
              src={user.photo_url || undefined}
              sx={{ width: 52, height: 52, bgcolor: color + "1f", color }}
            >
              {getRoleIcon(user.role)}
            </Avatar>
            <Box
              sx={{
                position: "absolute",
                right: -1,
                bottom: -1,
                width: 14,
                height: 14,
                borderRadius: "50%",
                border: "2px solid",
                borderColor: "background.paper",
                bgcolor: user.is_active ? "#16a34a" : "#cbd5e1",
              }}
            />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontWeight: 700 }} noWrap title={user.full_name || ""}>
              {user.full_name || "Без имени"}
            </Typography>
            <Chip
              icon={getRoleIcon(user.role)}
              label={getRoleLabel(user.role)}
              size="small"
              sx={{
                mt: 0.5,
                height: 22,
                bgcolor: color + "18",
                color,
                fontWeight: 600,
                "& .MuiChip-icon": { color: "inherit", fontSize: 15 },
              }}
            />
          </Box>
          <Box sx={{ display: "flex", flexShrink: 0 }}>
            <Tooltip title="Редактировать">
              <IconButton size="small" onClick={() => handleEditUser(user)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Удалить">
              <IconButton size="small" color="error" onClick={() => handleDeleteUser(user.id)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Контакты */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Link
            href={`tel:${user.phone}`}
            underline="hover"
            color="inherit"
            sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, fontSize: 14 }}
          >
            <PhoneIcon sx={{ fontSize: 15, color: "text.disabled" }} />
            {user.phone}
          </Link>
          {user.email && (
            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, color: "text.secondary", minWidth: 0 }}>
              <EmailIcon sx={{ fontSize: 15, color: "text.disabled" }} />
              <Typography variant="caption" noWrap>{user.email}</Typography>
            </Box>
          )}
        </Box>

        {/* Специализация */}
        {spec && (
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
              Специализация
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>{spec}</Typography>
          </Box>
        )}

        {/* График работы (мастера) */}
        {isMaster && (
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.75 }}>
              <Typography variant="caption" color="text.secondary">График работы</Typography>
              <Chip
                size="small"
                label={today ? `Сегодня ${hourNum(today.open)}–${hourNum(today.close)}` : "Сегодня выходной"}
                sx={{
                  height: 20,
                  fontSize: 11,
                  fontWeight: 600,
                  bgcolor: today ? "#16a34a18" : "#94a3b81f",
                  color: today ? "#16a34a" : "#64748b",
                }}
              />
            </Box>
            {hasSchedule ? (
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 0.5 }}>
                {DAY_DEFS.map((d) => {
                  const dh = dayHours(user.working_hours, d.key);
                  const isToday = d.key === TODAY_KEY;
                  return (
                    <Box
                      key={d.key}
                      sx={{
                        textAlign: "center",
                        py: 0.5,
                        borderRadius: 1.5,
                        bgcolor: dh ? color + "14" : "action.hover",
                        border: "1px solid",
                        borderColor: isToday ? color + "99" : "transparent",
                      }}
                    >
                      <Typography sx={{ fontSize: 10, fontWeight: 700, color: dh ? color : "text.disabled" }}>
                        {d.short}
                      </Typography>
                      <Typography sx={{ fontSize: 10, lineHeight: 1.3, color: dh ? "text.secondary" : "text.disabled" }}>
                        {dh ? `${hourNum(dh.open)}–${hourNum(dh.close)}` : "—"}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            ) : (
              <Typography variant="caption" color="text.disabled">График не задан</Typography>
            )}
          </Box>
        )}

        {/* Подвал — статус */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mt: "auto",
            pt: 1.5,
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography
            variant="body2"
            sx={{ fontWeight: 600 }}
            color={user.is_active ? "success.main" : "text.secondary"}
          >
            {user.is_active ? "Активен" : "Неактивен"}
          </Typography>
          <Switch checked={user.is_active} onChange={() => handleToggleStatus(user)} size="small" />
        </Box>
      </Paper>
    );
  };

  if (loading) {
    return (
      <Box sx={{ width: "100%" }}>
        <Header title="Пользователи" />
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ width: "100%", minHeight: "100vh", bgcolor: "background.default" }}>
        <Header title="Пользователи" />

        <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1200, mx: "auto" }}>
          <Breadcrumbs sx={{ mb: 3 }}>
            <Link color="inherit" href="#" onClick={() => navigate("/")} sx={{ display: "inline-flex", alignItems: "center" }}>
              <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              Главная
            </Link>
            <Typography color="text.primary" sx={{ display: "inline-flex", alignItems: "center" }}>
              <UsersIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              Пользователи
            </Typography>
          </Breadcrumbs>

          {/* Заголовок */}
          <Box sx={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: 2, mb: 3 }}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: "-0.02em" }}>
                Пользователи
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Мастера и администраторы салона
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateUser}
              sx={{ borderRadius: 2, px: 2.5, py: 1, boxShadow: "none" }}
            >
              Добавить
            </Button>
          </Box>

          {/* Статистика */}
          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            {stats.map((s) => (
              <Grid item xs={6} md={3} key={s.label}>
                <Paper
                  variant="outlined"
                  sx={{ p: 2.5, borderRadius: 3, display: "flex", alignItems: "center", gap: 2, height: "100%" }}
                >
                  <Box
                    sx={{
                      width: 46,
                      height: 46,
                      borderRadius: 2.5,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: s.color + "14",
                      color: s.color,
                      flexShrink: 0,
                    }}
                  >
                    {s.icon}
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.1 }}>{s.value}</Typography>
                    <Typography variant="body2" color="text.secondary" noWrap>{s.label}</Typography>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {/* Поиск + фильтр */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", mb: 3 }}>
            <TextField
              placeholder="Поиск по имени, телефону или роли…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              size="small"
              sx={{ flex: "1 1 280px", maxWidth: 420, "& .MuiOutlinedInput-root": { borderRadius: 2.5 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: "text.disabled" }} />
                  </InputAdornment>
                ),
              }}
            />
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              {([
                { key: "all", label: "Все" },
                { key: "master", label: "Мастера" },
                { key: "admin", label: "Администраторы" },
              ] as const).map((f) => (
                <Chip
                  key={f.key}
                  label={f.label}
                  onClick={() => setRoleFilter(f.key)}
                  variant={roleFilter === f.key ? "filled" : "outlined"}
                  color={roleFilter === f.key ? "primary" : "default"}
                  sx={{ fontWeight: 600, borderRadius: 2 }}
                />
              ))}
            </Box>
          </Box>

          {/* Список пользователей */}
          {visibleUsers.length === 0 ? (
            <Paper variant="outlined" sx={{ p: 8, textAlign: "center", color: "text.secondary", borderRadius: 3 }}>
              <UsersIcon sx={{ fontSize: 48, opacity: 0.25, mb: 1 }} />
              <Typography>{search || roleFilter !== "all" ? "Ничего не найдено" : "Пока нет пользователей"}</Typography>
            </Paper>
          ) : (
            groups.map((g) => (
              <Box key={g.role} sx={{ mb: 4 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{g.title}</Typography>
                  <Chip label={g.items.length} size="small" sx={{ height: 20, fontWeight: 700, bgcolor: "action.selected" }} />
                </Box>
                <Grid container spacing={2.5}>
                  {g.items.map((user) => (
                    <Grid item xs={12} sm={6} lg={4} key={user.id}>
                      {renderUserCard(user)}
                    </Grid>
                  ))}
                </Grid>
              </Box>
            ))
          )}
        </Box>
      </Box>

      {/* Dialog для создания/редактирования пользователя */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingUser ? "Редактировать пользователя" : "Добавить пользователя"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Имя"
                  value={formData.full_name || ''}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Телефон"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={formData.email || ''}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Роль</InputLabel>
                  <Select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value as "admin" | "master" })}
                  >
                    <MenuItem value="master">Мастер</MenuItem>
                    <MenuItem value="admin">Администратор</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {formData.role === "master" && (
                <>
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>Специализация (категории)</InputLabel>
                      <Select
                        multiple
                        value={formData.specialization_category_ids || []}
                        onChange={(e) => {
                          const value = e.target.value as number[];
                          setFormData({ ...formData, specialization_category_ids: value });
                        }}
                        renderValue={(selected) => {
                          const ids = selected as number[];
                          const names = serviceCategories
                            .filter(c => ids.includes(c.id))
                            .map(c => c.name);
                          return names.join(", ");
                        }}
                      >
                        {serviceCategories.map((c) => (
                          <MenuItem key={c.id} value={c.id}>
                            {c.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                      График работы
                    </Typography>

                    {Object.entries(dayNames).map(([key, label]) => {
                      const dayKey = key as keyof typeof dayNames;
                      const day = formData.working_hours?.[dayKey] || { works: false, open: "09:00", close: "21:00" };
                      return (
                        <Box key={key} sx={{ mb: 2, p: 1, border: "1px solid #eee", borderRadius: 1 }}>
                          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {label}
                            </Typography>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={Boolean(day.works)}
                                  onChange={(e) => {
                                    setFormData(prev => ({
                                      ...prev,
                                      working_hours: {
                                        ...(prev.working_hours || {}),
                                        [dayKey]: { ...day, works: e.target.checked },
                                      },
                                    }));
                                  }}
                                  size="small"
                                />
                              }
                              label={day.works ? "Работает" : "Выходной"}
                            />
                          </Box>

                          <Box display="flex" gap={2}>
                            <TextField
                              label="С"
                              type="time"
                              value={day.open || "09:00"}
                              onChange={(e) => {
                                setFormData(prev => ({
                                  ...prev,
                                  working_hours: {
                                    ...(prev.working_hours || {}),
                                    [dayKey]: { ...day, open: e.target.value },
                                  },
                                }));
                              }}
                              InputLabelProps={{ shrink: true }}
                              inputProps={{ step: 300 }}
                              size="small"
                              disabled={!day.works}
                            />
                            <TextField
                              label="До"
                              type="time"
                              value={day.close || "21:00"}
                              onChange={(e) => {
                                setFormData(prev => ({
                                  ...prev,
                                  working_hours: {
                                    ...(prev.working_hours || {}),
                                    [dayKey]: { ...day, close: e.target.value },
                                  },
                                }));
                              }}
                              InputLabelProps={{ shrink: true }}
                              inputProps={{ step: 300 }}
                              size="small"
                              disabled={!day.works}
                            />
                          </Box>
                        </Box>
                      );
                    })}
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                      Фото мастера
                    </Typography>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Avatar src={formData.photo_url || undefined} sx={{ width: 64, height: 64 }}>
                        <MasterIcon />
                      </Avatar>
                      <Button
                        component="label"
                        variant="outlined"
                        startIcon={<CloudUploadIcon />}
                        disabled={!editingUser}
                      >
                        Загрузить фото
                        <input
                          type="file"
                          hidden
                          accept="image/*"
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (f) void handleUploadPhoto(f);
                          }}
                        />
                      </Button>
                      {!editingUser && (
                        <Typography variant="caption" color="text.secondary">
                          Сначала создай пользователя, затем загрузи фото
                        </Typography>
                      )}
                    </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                      Права мастера
                    </Typography>
                    <Box display="flex" gap={3} flexWrap="wrap">
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData.can_cancel !== false}
                            onChange={(e) => setFormData({ ...formData, can_cancel: e.target.checked })}
                          />
                        }
                        label="Разрешить отмену"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData.can_reschedule !== false}
                            onChange={(e) => setFormData({ ...formData, can_reschedule: e.target.checked })}
                          />
                        }
                        label="Разрешить перенос"
                      />
                    </Box>
                  </Grid>
                </>
              )}
            </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Отмена</Button>
          <Button onClick={handleSaveUser} variant="contained">
            {editingUser ? "Сохранить" : "Создать"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar для уведомлений */}
      <Snackbar
        open={alert.show}
        autoHideDuration={6000}
        onClose={() => setAlert({ ...alert, show: false })}
      >
        <Alert
          onClose={() => setAlert({ ...alert, show: false })}
          severity={alert.severity}
          sx={{ width: "100%" }}
        >
          {alert.message}
        </Alert>
      </Snackbar>
    </>
  );
}
